// LocationFields — country plus the place field that depends on it: a county picker for Kenya,
// otherwise a free-text "City, state or county". Shared by the setup form and the Profile page.
// Switching country clears the field that no longer applies.
import { useState } from "react";
import { COUNTRIES, KENYA_COUNTRY_CODE } from "@dm-lms/shared/constants";
import { KENYA_COUNTIES, REGION_LABEL, flagSrc } from "../../lib/profileFormOptions.js";

interface LocationFieldsProps {
  country:     string;
  county:      string;
  region:      string;
  onCountry:   (value: string) => void;
  onCounty:    (value: string) => void;
  onRegion:    (value: string) => void;
  inputClass:  string;
}

// A fixed 24x18 box, always rendered so the layout never shifts. The image is hidden (the box stays)
// when the country has no flag file. Decorative: the select next to it carries the name.
function CountryFlag({ code }: { code: string }) {
  const [failedCode, setFailedCode] = useState<string | null>(null);
  const showImage = code !== "" && failedCode !== code;

  return (
    <span className="mt-1 inline-block h-[18px] w-6 shrink-0 overflow-hidden rounded-sm">
      {showImage && (
        <img
          src={flagSrc(code)}
          alt=""
          width={24}
          height={18}
          loading="lazy"
          onError={() => setFailedCode(code)}
          className="h-[18px] w-6 rounded-sm border border-gray-200 object-cover"
        />
      )}
    </span>
  );
}

export default function LocationFields({
  country, county, region, onCountry, onCounty, onRegion, inputClass,
}: LocationFieldsProps) {
  const inKenya = country === KENYA_COUNTRY_CODE;

  function changeCountry(value: string) {
    onCountry(value);
    onCounty("");
    onRegion("");
  }

  return (
    <>
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-dm-navy">
          Country
        </label>
        <div className="flex items-center gap-2">
          <CountryFlag code={country} />
          <select
            id="country"
            required
            value={country}
            onChange={(event) => changeCountry(event.target.value)}
            className={inputClass}
          >
            <option value="">Select…</option>
            {COUNTRIES.map(({ code, name }) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {country !== "" && inKenya && (
        <div>
          <label htmlFor="county" className="block text-sm font-medium text-dm-navy">
            County
          </label>
          <select id="county" required value={county} onChange={(event) => onCounty(event.target.value)} className={inputClass}>
            <option value="">Select…</option>
            {KENYA_COUNTIES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      {country !== "" && !inKenya && (
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-dm-navy">
            {REGION_LABEL}
          </label>
          <input
            id="region"
            type="text"
            required
            value={region}
            onChange={(event) => onRegion(event.target.value)}
            className={inputClass}
          />
        </div>
      )}
    </>
  );
}
